import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Admin.css";
import "./Gallery.admin.css";
import { API_BASE_URL } from "../../config";

// Convert file to WebP using canvas
const convertToWebP = (file) => {
    return new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            canvas.toBlob(
                (blob) => {
                    URL.revokeObjectURL(url);
                    const webpFile = new File([blob], file.name.replace(/\.[^.]+$/, ".webp"), { type: "image/webp" });
                    resolve(webpFile);
                },
                "image/webp",
                0.85
            );
        };
        img.src = url;
    });
};

// Upload file to UploadThing
const uploadToUT = async (file) => {
    const formData = new FormData();
    formData.append("files", file);
    const res = await fetch(`${API_BASE_URL}/api/uploadthing`, {
        method: "POST",
        body: formData,
    });
    if (!res.ok) throw new Error("Upload failed");
    const data = await res.json();
    // UploadThing returns array
    const item = Array.isArray(data) ? data[0] : data;
    return { url: item.url || item.ufsUrl, key: item.key };
};

const AdminGallery = () => {
    const navigate = useNavigate();
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingFolder, setEditingFolder] = useState(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [form, setForm] = useState({ name: "", description: "", displayDate: "" });
    const [thumbFile, setThumbFile] = useState(null);
    const [thumbPreview, setThumbPreview] = useState("");
    const [uploadProgress, setUploadProgress] = useState("");

    useEffect(() => {
        const token = localStorage.getItem("admin_token");
        if (!token) { navigate("/admin/anubhava-montessori/qwerzxcv6909/login"); return; }
        fetchFolders();
    }, [navigate]);

    const fetchFolders = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/gallery/folders`);
            const data = await res.json();
            setFolders(data);
        } catch { }
        setLoading(false);
    };

    const handleLogout = () => {
        localStorage.removeItem("admin_token");
        navigate("/admin/anubhava-montessori/qwerzxcv6909/login");
    };

    const openCreate = () => {
        setEditingFolder(null);
        setForm({ name: "", description: "", displayDate: "" });
        setThumbFile(null);
        setThumbPreview("");
        setShowModal(true);
    };

    const openEdit = (folder) => {
        setEditingFolder(folder);
        setForm({ name: folder.name, description: folder.description || "", displayDate: folder.displayDate || "" });
        setThumbFile(null);
        setThumbPreview(folder.thumbnail || "");
        setShowModal(true);
    };

    const handleThumbChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setThumbFile(file);
        setThumbPreview(URL.createObjectURL(file));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { alert("Folder name is required."); return; }
        setSaveLoading(true);
        const token = localStorage.getItem("admin_token");
        try {
            let thumbnail = editingFolder?.thumbnail || "";
            let thumbnailKey = editingFolder?.thumbnailKey || "";

            if (thumbFile) {
                setUploadProgress("Converting thumbnail to WebP...");
                const webp = await convertToWebP(thumbFile);
                setUploadProgress("Uploading thumbnail...");
                const uploaded = await uploadToUT(webp);
                thumbnail = uploaded.url;
                thumbnailKey = uploaded.key;
                setUploadProgress("");
            }

            const method = editingFolder ? "PUT" : "POST";
            const url = editingFolder
                ? `${API_BASE_URL}/api/gallery/folders/${editingFolder._id}`
                : `${API_BASE_URL}/api/gallery/folders`;

            const res = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ ...form, thumbnail, thumbnailKey }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Save failed.");
            setShowModal(false);
            fetchFolders();
        } catch (err) {
            alert(err.message);
        }
        setSaveLoading(false);
        setUploadProgress("");
    };

    const handleDelete = async (id) => {
        const token = localStorage.getItem("admin_token");
        try {
            const res = await fetch(`${API_BASE_URL}/api/gallery/folders/${id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                setFolders((prev) => prev.filter((f) => f._id !== id));
                setDeleteConfirmId(null);
            }
        } catch { alert("Error deleting folder."); }
    };

    return (
        <div className="dashboard-container">
            {/* Sidebar */}
            <div className="dashboard-sidebar">
                <div className="sidebar-logo">Anubhava</div>
                <ul className="sidebar-menu">
                    <li className="sidebar-item" onClick={() => navigate("/admin/anubhava-montessori/qwerzxcv6909/blogs")}>
                        <i className="fa-solid fa-blog"></i><span>Manage Blogs</span>
                    </li>
                    <li className="sidebar-item" onClick={() => navigate("/admin/anubhava-montessori/qwerzxcv6909/students")}>
                        <i className="fa-solid fa-users"></i><span>Students CRM</span>
                    </li>
                    <li className="sidebar-item active">
                        <i className="fa-solid fa-images"></i><span>Gallery</span>
                    </li>
                    <li className="sidebar-item" onClick={() => navigate("/gallery")}>
                        <i className="fa-solid fa-globe"></i><span>Visit Website</span>
                    </li>
                </ul>
                <div className="sidebar-logout">
                    <button onClick={handleLogout} className="btn-secondary" style={{ width: "100%" }}>Log Out</button>
                </div>
            </div>

            {/* Main */}
            <div className="dashboard-content">
                <div className="dashboard-header">
                    <div>
                        <h1>Gallery Manager</h1>
                        <p style={{ color: "#777", fontSize: "15px", marginTop: "4px" }}>
                            {folders.length} folder{folders.length !== 1 ? "s" : ""}
                        </p>
                    </div>
                    <button className="new-blog-btn" onClick={openCreate}>
                        <i className="fa-solid fa-folder-plus"></i> New Folder
                    </button>
                </div>

                {loading ? (
                    <div className="students-loading">Loading gallery folders...</div>
                ) : folders.length === 0 ? (
                    <div className="students-empty">
                        <i className="fa-solid fa-images" style={{ fontSize: "56px", color: "#ddd", marginBottom: "16px" }}></i>
                        <h3>No folders yet</h3>
                        <p>Click <strong>New Folder</strong> to create your first gallery.</p>
                    </div>
                ) : (
                    <div className="gallery-admin-grid">
                        {folders.map((folder) => (
                            <div key={folder._id} className="gallery-admin-card">
                                <div
                                    className="gallery-admin-thumb"
                                    onClick={() => navigate(`/admin/anubhava-montessori/qwerzxcv6909/gallery/${folder.folderId}`)}
                                    style={{ cursor: "pointer" }}
                                >
                                    {folder.thumbnail
                                        ? <img src={folder.thumbnail} alt={folder.name} />
                                        : <div className="gallery-admin-thumb-placeholder"><i className="fa-solid fa-image"></i></div>
                                    }
                                    <div className="gallery-thumb-overlay">
                                        <span><i className="fa-solid fa-folder-open"></i> Open Folder</span>
                                    </div>
                                </div>
                                <div className="gallery-admin-card-body">
                                    <h3>{folder.name}</h3>
                                    {folder.displayDate && <p className="gallery-folder-date"><i className="fa-regular fa-calendar"></i> {folder.displayDate}</p>}
                                    {folder.description && <p className="gallery-folder-desc">{folder.description}</p>}
                                    <p className="gallery-folder-id">ID: {folder.folderId}</p>
                                    <div className="gallery-admin-actions">
                                        <button className="action-icon-btn edit" onClick={() => openEdit(folder)} title="Edit folder">
                                            <i className="fa-solid fa-pencil"></i>
                                        </button>
                                        <button
                                            className="btn-secondary"
                                            style={{ fontSize: "13px", padding: "6px 14px" }}
                                            onClick={() => navigate(`/admin/anubhava-montessori/qwerzxcv6909/gallery/${folder.folderId}`)}
                                        >
                                            <i className="fa-solid fa-photo-film"></i> Manage Images
                                        </button>
                                        {deleteConfirmId === folder._id ? (
                                            <div className="delete-confirm-inline">
                                                <span>Delete all?</span>
                                                <button className="confirm-yes-btn" onClick={() => handleDelete(folder._id)}>Yes</button>
                                                <button className="confirm-no-btn" onClick={() => setDeleteConfirmId(null)}>No</button>
                                            </div>
                                        ) : (
                                            <button className="action-icon-btn delete" onClick={() => setDeleteConfirmId(folder._id)} title="Delete folder">
                                                <i className="fa-solid fa-trash-can"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingFolder ? "Edit Folder" : "Create New Folder"}</h2>
                            <button className="modal-close-btn" onClick={() => setShowModal(false)}><i className="fa-solid fa-xmark"></i></button>
                        </div>
                        <form onSubmit={handleSave} className="modal-form">
                            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                                <div className="form-group">
                                    <label>Folder Name <span className="required-star">*</span></label>
                                    <input type="text" placeholder="e.g. Annual Day 2025" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} required />
                                </div>
                                <div className="form-group">
                                    <label>Display Date</label>
                                    <input type="text" placeholder="e.g. March 2025" value={form.displayDate} onChange={(e) => setForm(p => ({ ...p, displayDate: e.target.value }))} />
                                </div>
                                <div className="form-group">
                                    <label>Description</label>
                                    <textarea rows={3} placeholder="Short description of this gallery..." value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} style={{ resize: "vertical" }} />
                                </div>
                                <div className="form-group">
                                    <label>Thumbnail Image</label>
                                    <div className="thumb-upload-area" onClick={() => document.getElementById("thumb-input").click()}>
                                        {thumbPreview
                                            ? <img src={thumbPreview} alt="preview" style={{ width: "100%", height: "160px", objectFit: "cover", borderRadius: "10px" }} />
                                            : <div className="thumb-upload-placeholder">
                                                <i className="fa-solid fa-cloud-arrow-up"></i>
                                                <span>Click to upload thumbnail</span>
                                                <small>Will be converted to WebP</small>
                                            </div>
                                        }
                                    </div>
                                    <input id="thumb-input" type="file" accept="image/*" style={{ display: "none" }} onChange={handleThumbChange} />
                                </div>
                            </div>
                            {uploadProgress && <p className="upload-progress-text"><i className="fa-solid fa-spinner fa-spin"></i> {uploadProgress}</p>}
                            <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary" disabled={saveLoading}>
                                    {saveLoading ? "Saving..." : editingFolder ? "Update Folder" : "Create Folder"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminGallery;
